import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import Button from '../ui/Button';
import {
  ASSISTANT_COUNTER_VISIBLE_FROM,
  ASSISTANT_MESSAGE_MAX_LENGTH,
} from '../../services/aiService';

/** Id of the character counter, referenced by the box's aria-describedby. */
const COUNTER_ID = 'assistant-counter';

/**
 * Whether Enter should submit rather than insert a line break.
 *
 * A physical keyboard has Shift+Enter for a newline, so Enter submits. A touch
 * keyboard has no Shift, so intercepting Enter there would leave a visitor on a
 * phone with no way to write a second line -- the Send button submits instead.
 * matchMedia is feature-detected: without it, assume a physical keyboard.
 */
const shouldSendOnEnter = () => {
  if (typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(pointer: coarse)').matches;
};

/**
 * Grows the chat box to fit its content, up to the max-height in
 * variables.css, past which the box scrolls instead.
 *
 * The reset to `auto` comes first: without it the element could only ever grow,
 * because scrollHeight never reports less than the current height. The border
 * offset matters too -- scrollHeight excludes borders, and the global
 * border-box reset would otherwise leave the content 2px short, which shows up
 * as a permanent scrollbar.
 */
const fitTextareaToContent = (node) => {
  if (!node) return;
  node.style.height = 'auto';
  node.style.height = `${node.scrollHeight + (node.offsetHeight - node.clientHeight)}px`;
};

/**
 * The assistant conversation: transcript and input.
 *
 * Purely presentational. All state is owned by the caller -- useAssistantChat,
 * called in AssistantWidget -- which is what lets the transcript survive the
 * panel being closed and reopened. A modal that discarded the conversation when
 * dismissed would be hostile.
 *
 * One interaction is forwarded rather than rendered: `onOpenContact`. The contact
 * modal is a layer owned by AssistantWidget, and the element the click came from
 * is what takes focus back when that modal closes, so the handler is handed the
 * click event untouched. Required: a trigger with no handler is precisely the
 * dead button this modal replaces.
 *
 * The transcript is a role="log" live region, so answers are announced without
 * stealing focus from whatever the visitor is doing.
 */
const AssistantConversation = ({
  messages,
  input,
  onInputChange,
  onSend,
  isSending,
  isConfigured,
  isExpanded = false,
  onOpenContact,
}) => {
  const transcriptRef = useRef(null);
  const textareaRef = useRef(null);

  // Keep the newest message in view. Only the transcript scrolls, so arriving
  // answers never move the page behind the panel. Expanding the panel gives the
  // transcript more room, which is another reason to come back to the bottom.
  useEffect(() => {
    const transcript = transcriptRef.current;
    if (transcript) transcript.scrollTop = transcript.scrollHeight;
  }, [messages, isSending, isExpanded]);

  // Fit the box to the draft after every change: typing, pasting, and clearing
  // all arrive as an `input` change. isExpanded is in the list because the
  // panel's width changes how the draft wraps.
  useEffect(() => {
    fitTextareaToContent(textareaRef.current);
  }, [input, isExpanded]);

  // A window resize re-wraps the draft at a new width (and covers rotating a
  // phone), so the height has to be measured again.
  useEffect(() => {
    const handleResize = () => fitTextareaToContent(textareaRef.current);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /** One submit path, shared by the Send button and the Enter key. */
  const submit = () => {
    if (!isSending) onSend(input);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submit();
  };

  /**
   * Enter submits; Shift+Enter inserts a line break. shouldSendOnEnter explains
   * why a touch keyboard is exempt from that rule.
   */
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    // Enter also commits an IME composition (CJK input, predictive text), and
    // sending there would post a half-finished word. 229 is what some Android
    // keyboards report for the same thing.
    const { isComposing, keyCode } = event.nativeEvent || {};
    if (isComposing || keyCode === 229) return;

    if (event.shiftKey || !shouldSendOnEnter()) return;

    // Required: without this the textarea inserts a newline before the form
    // ever submits.
    event.preventDefault();
    submit();
  };

  // The server truncates at ASSISTANT_MESSAGE_MAX_LENGTH, so the count appears
  // while there is still time to shorten the draft -- not at the limit itself.
  const showCounter = input.length > ASSISTANT_COUNTER_VISIBLE_FROM;

  // The same element in both branches: the way to a person does not depend on
  // the assistant being configured, and it belongs in the button row when there
  // is one. No size prop, so it matches Send's height in that row.
  //
  // A button, not a route: it opens the contact modal, which offers the phone and
  // the email without ending the conversation or navigating away from the page
  // being read. The click event goes straight to the caller, which remembers the
  // element it came from and returns focus to it when the modal closes.
  const personButton = (
    <Button variant="secondary" onClick={onOpenContact}>
      Talk to a Person Instead
    </Button>
  );

  return (
    <div className="assistant-conversation">
      <div
        className="assistant-transcript"
        role="log"
        aria-live="polite"
        aria-label="Assistant conversation"
        ref={transcriptRef}
      >
        {messages.map((message) => (
          <p
            key={message.id}
            className={`assistant-message assistant-message--${message.role}${
              message.isError ? ' assistant-message--error' : ''
            }`}
          >
            {message.isError ? (
              <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" />
            ) : null}
            {message.text}
          </p>
        ))}

        {isSending ? (
          <p className="assistant-message assistant-message--assistant assistant-message--pending">
            <FontAwesomeIcon icon={faCircleNotch} spin aria-hidden="true" />
            Thinking&hellip;
          </p>
        ) : null}
      </div>

      {isConfigured ? (
        <form className="assistant-form" onSubmit={handleSubmit}>
          <label className="visually-hidden" htmlFor="assistant-question">
            Ask the assistant a question
          </label>
          <textarea
            id="assistant-question"
            name="assistantQuestion"
            className="form-control assistant-input"
            rows={1}
            value={input}
            onChange={(event) => onInputChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about services, timelines, or getting started"
            autoComplete="off"
            enterKeyHint="send"
            ref={textareaRef}
            maxLength={ASSISTANT_MESSAGE_MAX_LENGTH}
            aria-describedby={showCounter ? COUNTER_ID : undefined}
            disabled={isSending}
          />
          {showCounter ? (
            <p className="assistant-counter" id={COUNTER_ID}>
              {input.length} / {ASSISTANT_MESSAGE_MAX_LENGTH}
            </p>
          ) : null}
          <div className="assistant-form__actions">
            {personButton}
            <Button type="submit" variant="primary" disabled={isSending}>
              Send
            </Button>
          </div>
        </form>
      ) : (
        <>
          <p className="assistant-notice text-small">
            The assistant is not enabled in this build. Please use the consultation
            form or contact us directly and a person will help you.
          </p>
          <div className="assistant-actions">{personButton}</div>
        </>
      )}
    </div>
  );
};

export default AssistantConversation;
